import React, { useState } from 'react';
import { Clock, FileText, Video, Archive, Download, CheckCircle, X } from 'lucide-react';
import { api } from '../../api';
import { TermsOfUseModal } from './TermsOfUseModal';
import { ScheduleWorkshopPrompt } from './WorkshopScheduleModals';

const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-medium text-gray-700">{value}</p>
  </div>
);

export const CourseDetailModal = ({ course, onClose, hasAccess, user, onScheduleWorkshop, onTermsAccept }) => {
  const [downloading, setDownloading] = useState(null);
  const [showSchedulePrompt, setShowSchedulePrompt] = useState(false);
  const [downloadedCourse, setDownloadedCourse] = useState(null);
  const [showTermsForDownload, setShowTermsForDownload] = useState(false);
  const [pendingFileId, setPendingFileId] = useState(null);
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  const downloadFile = async (fileId) => {
    setDownloading(fileId);
    try {
      const result = await api.request(`/courses/${course.id}/files/${fileId}/download`);
      window.open(result.download_url, '_blank');
      setDownloadedCourse(course);
      setShowSchedulePrompt(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadClick = (fileId) => {
    if (!user.terms_accepted) {
      setPendingFileId(fileId);
      setShowTermsForDownload(true);
    } else {
      downloadFile(fileId);
    }
  };

  const handleTermsAcceptAndDownload = async () => {
    setAcceptingTerms(true);
    try {
      await api.request('/auth/accept-terms', { method: 'PUT' });
      setShowTermsForDownload(false);
      if (onTermsAccept) onTermsAccept();
      if (pendingFileId) {
        const fid = pendingFileId;
        setPendingFileId(null);
        await downloadFile(fid);
      }
    } catch (err) {
      alert('Failed to save terms acceptance. Please try again.');
    } finally {
      setAcceptingTerms(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('Video')) return Video;
    if (fileType.includes('ZIP')) return Archive;
    return FileText;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (showSchedulePrompt && downloadedCourse) {
    return (
      <ScheduleWorkshopPrompt
        course={downloadedCourse}
        onSchedule={() => { setShowSchedulePrompt(false); onClose(); if (onScheduleWorkshop) onScheduleWorkshop(downloadedCourse); }}
        onClose={() => setShowSchedulePrompt(false)}
      />
    );
  }

  if (showTermsForDownload) {
    return (
      <TermsOfUseModal
        onAccept={handleTermsAcceptAndDownload}
        onDecline={() => { setShowTermsForDownload(false); setPendingFileId(null); }}
        accepting={acceptingTerms}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="h-2 bg-levelup-dark"></div>
        <div className="p-6 border-b bg-gray-50">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-levelup-dark">{course.title}</h2>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="px-2 py-1 bg-levelup-dark/10 text-levelup-dark rounded text-sm">{course.solution_area}</span>
                <span className="px-2 py-1 bg-levelup-accent/20 text-levelup-darker rounded text-sm">{course.course_type}</span>
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">{course.level}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">Last Updated: {formatDate(course.updated_at)}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg"><X size={24} /></button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <InfoItem label="Duration" value={course.duration} />
            <InfoItem label="Language" value={course.language} />
            <InfoItem label="Target Role" value={course.target_role} />
            <InfoItem label="Target Audience" value={course.target_audience} />
            <InfoItem label="Hands-on Lab" value={course.hands_on_lab ? 'Yes' : 'No'} />
            <InfoItem label="Certification" value={course.certification_course ? 'Yes' : 'No'} />
          </div>

          <div className="mb-6">
            <h3 className="font-semibold text-levelup-dark mb-2">Description</h3>
            <p className="text-gray-600">{course.description}</p>
          </div>

          <div className="mb-6">
            <h3 className="font-semibold text-levelup-dark mb-3">Course Files ({course.files?.length || 0})</h3>
            {course.files?.length > 0 ? (
              <div className="space-y-2">
                {course.files.map(file => {
                  const FileIcon = getFileIcon(file.file_type);
                  const uploadDate = file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null;
                  return (
                    <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileIcon className="text-levelup-dark" size={20} />
                        <div>
                          <p className="font-medium text-sm">{file.original_name}</p>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span>{file.file_type}</span>
                            {uploadDate && <><span>•</span><span>Published: {uploadDate}</span></>}
                          </div>
                        </div>
                      </div>
                      {hasAccess && (
                        <button onClick={() => handleDownloadClick(file.id)} disabled={downloading === file.id}
                          className="flex items-center space-x-1 px-3 py-1 bg-levelup-accent text-levelup-darker rounded-lg text-sm hover:bg-levelup-accent-hover disabled:opacity-50"
                          data-testid={`download-file-${file.id}`}>
                          <Download size={14} />
                          <span>{downloading === file.id ? 'Loading...' : 'Download'}</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No files uploaded yet.</p>
            )}
          </div>

          {user.role === 'training_partner' && !hasAccess && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Clock className="text-yellow-500" size={20} />
                <span className="font-medium text-yellow-800">Your portal access is pending approval. Once approved, you'll be able to download all content.</span>
              </div>
            </div>
          )}

          {hasAccess && user.role === 'training_partner' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="text-green-500" size={20} />
                <span className="font-medium text-green-800">You have portal access - download any content</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
